import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Overlay, Popover } from "react-bootstrap";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import SearchableTable from "../shared/SearchableTable";
import { createPostgRESTDataAdapter } from "../shared/dataAdapters";
import ModerationActionPanel from "./ModerationActionPanel";
import ModerationDetail from "./ModerationDetail";
import ModerationRulesPanel from "./ModerationRulesPanel";

const HOVER_OPEN_DELAY_MS = 150;
const HOVER_CLOSE_DELAY_MS = 200;

const SUPABASE_URL = () => globalThis.Qwoted.SUPABASE_API_EXTERNAL_URL;

const TABS = [
  { key: "attention", label: "Needs Attention" },
  { key: "posts", label: "Posts" },
  { key: "comments", label: "Comments" },
  { key: "all", label: "All" },
  // The rule editor is a different surface (edits the policy, not reported content),
  // so this tab bypasses the content-queue table below and renders its own panel.
  { key: "rules", label: "Rules" },
];

const VALID_TAB_KEYS = new Set(TABS.map((tab) => tab.key));

// Every key must be a real column on admin_moderation_queue (the adapter selects
// all of them). The composite "Content" cell is keyed on content_preview and
// enriched from the hidden columns; the inline actions come from renderActions.
const COLUMNS = {
  reportable_type: { type: "string", display_as: "Type" },
  reportable_id: { type: "string", hidden: true },
  content_preview: { type: "string", display_as: "Content" },
  author_name: { type: "string", hidden: true },
  distinct_reasons: { type: "string", display_as: "Reasons" },
  unreviewed_report_count: { type: "number", display_as: "Reports" },
  total_report_count: { type: "number", hidden: true },
  current_status: { type: "string", display_as: "Status" },
  latest_moderation_action: { type: "string", hidden: true },
  latest_moderation_reason: { type: "string", hidden: true },
  latest_moderation_at: { type: "datetime", hidden: true },
  first_reported_at: { type: "datetime", hidden: true },
  last_reported_at: { type: "datetime", display_as: "Last reported" },
};

const TAB_CONFIGS = {
  attention: {
    staticFilters: { unreviewed_report_count: "gt.0" },
    defaultSort: { last_reported_at: "desc" },
  },
  posts: {
    staticFilters: { reportable_type: "eq.Interact::FeedPost" },
    defaultSort: { last_reported_at: "desc" },
  },
  comments: {
    staticFilters: { reportable_type: "eq.Interact::Comment" },
    defaultSort: { last_reported_at: "desc" },
  },
  all: {
    staticFilters: {},
    defaultSort: { last_reported_at: "desc" },
  },
};

function getTabFromUrl() {
  const tab = new URLSearchParams(window.location.search).get("tab");
  return tab && VALID_TAB_KEYS.has(tab) ? tab : "attention";
}

function setTabInUrl(tab) {
  const url = new URL(window.location);
  url.searchParams.set("tab", tab);
  window.history.replaceState({}, "", url);
}

function statusBadge(status) {
  const map = {
    visible: "bg-success",
    ghosted: "bg-danger",
    pending_manual_review: "bg-warning text-dark",
    deleted: "bg-dark",
    unprocessed: "bg-info text-dark",
  };
  return <span className={`badge ${map[status] || "bg-secondary"}`}>{status}</span>;
}

const TYPE_LABELS = { "Interact::FeedPost": "Post", "Interact::Comment": "Comment" };

function typeLabel(type) {
  return TYPE_LABELS[type] || type;
}

// distinct_reasons is a ", "-joined string from the view; render each as a chip.
function reasonChips(reasons) {
  if (!reasons) return <span className="text-muted">—</span>;
  return reasons.split(", ").map((reason) => (
    <span key={reason} className="badge bg-light text-dark border me-1 mb-1">
      {reason}
    </span>
  ));
}

// The reports-count cell doubles as the trigger for the per-report detail, shown in a
// Bootstrap popover. It opens on hover (after a short delay) and STAYS open while the
// pointer is over either the count or the popover body — so the report list is readable
// and scrollable — closing shortly after the pointer leaves both. A click pins it open
// (surviving pointer-leave) until it's clicked again or dismissed by an outside click.
// Reporter PII (in ModerationDetail) is fetched lazily, only once the popover opens.
function ReportsPopover({ item }) {
  const target = useRef(null);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);
  const [show, setShow] = useState(false);
  const [pinned, setPinned] = useState(false);

  const clearTimers = () => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
  };
  useEffect(() => clearTimers, []);

  const scheduleOpen = () => {
    clearTimers();
    openTimer.current = setTimeout(() => setShow(true), HOVER_OPEN_DELAY_MS);
  };
  const scheduleClose = () => {
    clearTimeout(openTimer.current);
    if (pinned) return; // a pinned popover ignores pointer-leave
    closeTimer.current = setTimeout(() => setShow(false), HOVER_CLOSE_DELAY_MS);
  };
  const cancelClose = () => clearTimeout(closeTimer.current);
  const togglePin = () => {
    clearTimers();
    if (pinned) {
      setPinned(false);
      setShow(false);
    } else {
      setPinned(true);
      setShow(true);
    }
  };
  const dismiss = () => {
    clearTimers();
    setPinned(false);
    setShow(false);
  };

  const unreviewed = Number(item.unreviewed_report_count);
  const total = Number(item.total_report_count);

  return (
    <>
      <button
        ref={target}
        type="button"
        className="btn btn-link p-0 text-decoration-none"
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
        onFocus={() => setShow(true)}
        onBlur={scheduleClose}
        onClick={togglePin}
        aria-expanded={show}
        title="View the individual reports"
      >
        <strong>{unreviewed}</strong>
        {total !== unreviewed && <span className="text-muted"> ({total} total)</span>}
      </button>
      <Overlay
        target={target.current}
        show={show}
        placement="bottom-start"
        rootClose={pinned}
        onHide={dismiss}
        transition={false}
      >
        <Popover
          id={`reports-${item.reportable_type}-${item.reportable_id}`}
          style={{ maxWidth: 420 }}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          <Popover.Header as="h6" className="small mb-0 d-flex justify-content-between align-items-center">
            <span>
              Reports — {typeLabel(item.reportable_type)}
              <span className="text-muted"> by {item.author_name || "Unknown author"}</span>
            </span>
            {pinned && (
              <button type="button" className="btn-close btn-close-sm ms-2" aria-label="Close" onClick={dismiss} />
            )}
          </Popover.Header>
          <Popover.Body className="small" style={{ maxHeight: 320, overflowY: "auto" }}>
            <ModerationDetail reportableType={item.reportable_type} reportableId={item.reportable_id} />
          </Popover.Body>
        </Popover>
      </Overlay>
    </>
  );
}

ReportsPopover.propTypes = {
  item: PropTypes.shape({
    reportable_type: PropTypes.string.isRequired,
    reportable_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    unreviewed_report_count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    total_report_count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    author_name: PropTypes.string,
  }).isRequired,
};

function ModerationHubPanel({ ruleActionOptions = [], severeReasons = [], seededDefaults = [] }) {
  const [activeTab, setActiveTab] = useState(getTabFromUrl);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [attentionCount, setAttentionCount] = useState(null);
  const isRulesTab = activeTab === "rules";

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    setTabInUrl(tab);
  }, []);

  const refresh = useCallback(() => setRefreshTrigger((prev) => prev + 1), []);

  // Live count for the "Needs Attention" tab badge: how many items still have
  // unreviewed reports (the same filter the tab itself applies). Fetched
  // independently of the active tab, and re-fetched whenever an action changes the
  // queue (refreshTrigger), so acting on the last flagged item clears the badge. A
  // zero-row PostgREST request with an exact count read from the Content-Range header.
  useEffect(() => {
    let active = true;
    const params = new URLSearchParams({ ...TAB_CONFIGS.attention.staticFilters, select: "reportable_id" });
    fetch(`${SUPABASE_URL()}/rest/v1/admin_moderation_queue?${params}`, {
      headers: { ...authenticatedHeadersHash(), Prefer: "count=exact", Range: "0-0" },
    })
      .then((res) => {
        const total = res.headers.get("Content-Range")?.split("/")[1];
        if (active && total && total !== "*") setAttentionCount(parseInt(total, 10));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [refreshTrigger]);

  // The rules tab has no content-queue config; fall back so the (unconditional)
  // hooks below stay safe — its table simply isn't rendered.
  const tabConfig = TAB_CONFIGS[activeTab] || TAB_CONFIGS.all;

  const dataAdapter = useMemo(
    () =>
      createPostgRESTDataAdapter({
        endpoint: `${SUPABASE_URL()}/rest/v1`,
        resource: "admin_moderation_queue",
        headers: authenticatedHeadersHash(),
        staticFilters: tabConfig.staticFilters,
        columns: COLUMNS,
        searchColumns: ["content_preview", "author_name", "distinct_reasons"],
      }),
    [tabConfig.staticFilters],
  );

  const renderCell = (row, column) => {
    if (column === "content_preview") {
      return (
        <div>
          <div className="fw-medium">{row.content_preview || "—"}</div>
          <div className="text-muted small">by {row.author_name || "Unknown author"}</div>
        </div>
      );
    }
    if (column === "reportable_type") return typeLabel(row.reportable_type);
    if (column === "current_status") return statusBadge(row.current_status);
    if (column === "distinct_reasons") return reasonChips(row.distinct_reasons);
    if (column === "unreviewed_report_count") {
      // Unread-style: bold unreviewed count + total when they differ. The number is
      // the trigger — hover (or click to pin) opens the per-report detail popover.
      return <ReportsPopover item={row} />;
    }
    return undefined;
  };

  const renderActions = (row) => <ModerationActionPanel item={row} onActionComplete={refresh} />;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="mb-0">Content Moderation</h1>
      </div>

      <ul className="nav nav-tabs mb-0">
        {TABS.map((tab) => (
          <li className="nav-item" key={tab.key}>
            <button
              type="button"
              className={`nav-link ${activeTab === tab.key ? "active" : ""}`}
              onClick={() => handleTabChange(tab.key)}
            >
              {tab.label}
              {tab.key === "attention" && attentionCount > 0 && (
                <span className="badge rounded-pill bg-danger ms-2">{attentionCount}</span>
              )}
            </button>
          </li>
        ))}
      </ul>

      {isRulesTab ? (
        <div className="mt-3">
          <ModerationRulesPanel
            actionOptions={ruleActionOptions}
            severeReasons={severeReasons}
            seededDefaults={seededDefaults}
          />
        </div>
      ) : (
        <SearchableTable
          columns={COLUMNS}
          searchColumns={["content_preview", "author_name", "distinct_reasons"]}
          dataAdapter={dataAdapter}
          pageSize={25}
          defaultSort={tabConfig.defaultSort}
          renderCell={renderCell}
          renderActions={renderActions}
          enableFiltering={false}
          refreshTrigger={refreshTrigger}
        />
      )}
    </div>
  );
}

ModerationHubPanel.propTypes = {
  ruleActionOptions: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })),
  severeReasons: PropTypes.arrayOf(PropTypes.string),
  seededDefaults: PropTypes.arrayOf(
    PropTypes.shape({
      reason: PropTypes.string,
      reportable_type: PropTypes.string,
      action: PropTypes.string,
      threshold: PropTypes.number,
    }),
  ),
};

export default ModerationHubPanel;
